import { useMemo, useRef, useState } from 'react';
import { CalendarDays, Check, CheckCircle2, Circle, Columns3, Flag, Inbox, List, ListTodo, Plus, Search, Sun, Trash2, X } from 'lucide-react';
import { TASK_PRIORITIES, formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { addDays } from '../bookings/shared.jsx';
import { problem } from '../bookings/util.js';
import { changeTask, reloadTasks, useTasks } from './store.js';

// Tasks: your own to-do list (nobody else sees it). Views down the side
// (Today with anything overdue, Upcoming, Anytime, Completed) and your lists.
// Type a task and press Enter to add it to what you're looking at; click one
// to give it a deadline, priority, notes or steps. Board shows the same tasks
// as cards in To do / In progress / Done; drag a card to move it (Done ticks
// it off, and ticking one off moves it to Done).
const VIEWS = [
  { id: 'today', label: 'Today', icon: Sun },
  { id: 'upcoming', label: 'Upcoming', icon: CalendarDays },
  { id: 'anytime', label: 'Anytime', icon: Inbox },
  { id: 'done', label: 'Completed', icon: CheckCircle2 },
];
const COLOURS = ['#0B2340', '#2f6fd6', '#2e8b57', '#D9A400', '#c0392b', '#7b4fc9', '#e67e22', '#5f6b7a'];
const PRIORITY_LABEL = Object.fromEntries(TASK_PRIORITIES.map((p) => [p.value, p.label]));
const STAGES = [
  { id: 'todo', label: 'To do' },
  { id: 'doing', label: 'In progress' },
  { id: 'done', label: 'Done' },
];
const LAYOUT_KEY = 'nu.tasksLayout';
const weekday = (d) => new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));

// "Overdue · Mon 27/10/2026", "Today 09:30", "Tomorrow", "Tue 04/11/2026".
function dueLabel(t, today) {
  if (!t.dueDate) return null;
  const time = t.dueTime ? ` ${t.dueTime}` : '';
  if (!t.done && t.dueDate < today) return { text: `Overdue · ${weekday(t.dueDate)} ${formatDay(t.dueDate)}${time}`, tone: 'late' };
  if (t.dueDate === today) return { text: `Today${time}`, tone: 'today' };
  if (t.dueDate === addDays(today, 1)) return { text: `Tomorrow${time}`, tone: '' };
  return { text: `${weekday(t.dueDate)} ${formatDay(t.dueDate)}${time}`, tone: '' };
}

function TaskRow({ t, list, today, onOpen }) {
  const due = dueLabel(t, today);
  const stepsDone = t.steps.filter((s) => s.done).length;
  return (
    <li className={`task-row${t.done ? ' done' : ''} p-${t.priority}`}>
      <button type="button" className="task-check" aria-label={t.done ? `Mark "${t.title}" not done` : `Mark "${t.title}" done`} onClick={() => changeTask(t.id, { done: !t.done })}>
        {t.done ? <CheckCircle2 size={22} aria-hidden="true" /> : <Circle size={22} aria-hidden="true" />}
      </button>
      <button type="button" className="task-main" onClick={() => onOpen(t)}>
        <span className="task-title">{t.title}</span>
        <span className="task-meta">
          {t.stage === 'doing' && <span className="task-doing">In progress</span>}
          {due && <span className={`task-due ${due.tone}`}>{due.text}</span>}
          {t.priority !== 'none' && <span className={`task-flag p-${t.priority}`}><Flag size={12} aria-hidden="true" /> {PRIORITY_LABEL[t.priority]}</span>}
          {t.steps.length > 0 && <span className="small muted">{stepsDone}/{t.steps.length} steps</span>}
          {t.notes && <span className="small muted">Notes</span>}
          {list && <span className="task-list-chip" style={{ '--list': list.colour }}>{list.name}</span>}
        </span>
      </button>
    </li>
  );
}

// One task: everything about it.
function TaskModal({ task, lists, onClose }) {
  const [f, setF] = useState({ title: task.title, notes: task.notes || '', dueDate: task.dueDate || '', dueTime: task.dueTime || '', priority: task.priority, listId: task.listId ? String(task.listId) : '' });
  const [steps, setSteps] = useState(task.steps.map((s) => ({ title: s.title, done: s.done })));
  const [step, setStep] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const today = todayUk();

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const withStep = step.trim() ? [...steps, { title: step.trim(), done: false }] : steps;
      await api(`/tasks/${task.id}`, { method: 'PUT', body: { ...f, dueDate: f.dueDate || null, dueTime: f.dueDate && f.dueTime ? f.dueTime : null, listId: f.listId ? Number(f.listId) : null, steps: withStep } });
      await reloadTasks();
      onClose();
    } catch (err) {
      setError(problem(err));
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api(`/tasks/${task.id}`, { method: 'DELETE' });
      await reloadTasks();
      onClose();
    } catch (err) {
      setError(problem(err));
      setBusy(false);
    }
  };
  const addStep = () => { if (step.trim()) { setSteps((s) => [...s, { title: step.trim(), done: false }]); setStep(''); } };

  return (
    <Modal
      open
      title="Task"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn with-icon task-delete" disabled={busy} onClick={remove}><Trash2 size={16} aria-hidden="true" /> Delete</button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="task-form" className="btn primary" disabled={busy || !f.title.trim()}>Save</button>
        </>
      )}
    >
      <form id="task-form" className="form" onSubmit={save}>
        {error && <p className="notice-line notice-bad" role="alert">{error}</p>}
        <label className="fld">Task<input value={f.title} onChange={set('title')} maxLength={200} required /></label>
        <div className="fld-row">
          <label className="fld narrow">Due<input type="date" value={f.dueDate} onChange={set('dueDate')} /></label>
          <label className="fld narrow">Time<input type="time" value={f.dueTime} onChange={set('dueTime')} disabled={!f.dueDate} /></label>
          <label className="fld">
            Priority
            <select value={f.priority} onChange={set('priority')}>
              {TASK_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
        </div>
        <div className="task-quick-dates">
          {[['Today', today], ['Tomorrow', addDays(today, 1)], ['Next week', addDays(today, 7)]].map(([l, d]) => (
            <button key={l} type="button" className={`btn${f.dueDate === d ? ' active' : ''}`} onClick={() => setF((x) => ({ ...x, dueDate: d }))}>{l}</button>
          ))}
          {f.dueDate && <button type="button" className="link-btn" onClick={() => setF((x) => ({ ...x, dueDate: '', dueTime: '' }))}>No date</button>}
        </div>
        <label className="fld">
          List
          <select value={f.listId} onChange={set('listId')}>
            <option value="">Tasks (no list)</option>
            {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <fieldset className="fld-group">
          <legend>Steps</legend>
          {steps.length > 0 && (
            <ul className="task-steps">
              {steps.map((s, i) => (
                <li key={i}>
                  <label className="check"><input type="checkbox" checked={s.done} onChange={() => setSteps((x) => x.map((y, j) => (j === i ? { ...y, done: !y.done } : y)))} /> <span className={s.done ? 'step-done' : ''}>{s.title}</span></label>
                  <button type="button" className="btn icon-only" aria-label={`Remove step ${s.title}`} onClick={() => setSteps((x) => x.filter((_, j) => j !== i))}><X size={16} aria-hidden="true" /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="task-step-add">
            <input value={step} onChange={(e) => setStep(e.target.value)} maxLength={200} placeholder="Add a step" aria-label="New step" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStep(); } }} />
            <button type="button" className="btn" disabled={!step.trim()} onClick={addStep}>Add</button>
          </div>
        </fieldset>
        <label className="fld">Notes<textarea value={f.notes} onChange={set('notes')} maxLength={5000} rows={4} /></label>
      </form>
    </Modal>
  );
}

// Add or rename a list.
function ListModal({ list, onClose }) {
  const [name, setName] = useState(list?.name || '');
  const [colour, setColour] = useState(list?.colour || COLOURS[1]);
  const [error, setError] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (list) await api(`/tasks/lists/${list.id}`, { method: 'PUT', body: { name, colour } });
      else await api('/tasks/lists', { method: 'POST', body: { name, colour } });
      await reloadTasks();
      onClose(true);
    } catch (err) { setError(problem(err)); }
  };
  const remove = async () => {
    try {
      await api(`/tasks/lists/${list.id}`, { method: 'DELETE' });
      await reloadTasks();
      onClose('deleted');
    } catch (err) { setError(problem(err)); }
  };
  return (
    <Modal
      open
      title={list ? 'Edit list' : 'New list'}
      onClose={() => onClose(false)}
      footer={(
        <>
          {list && <button type="button" className="btn with-icon task-delete" onClick={remove}><Trash2 size={16} aria-hidden="true" /> Delete list</button>}
          <button type="button" className="btn" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" form="list-form" className="btn primary" disabled={!name.trim()}>Save</button>
        </>
      )}
    >
      <form id="list-form" className="form" onSubmit={save}>
        {error && <p className="notice-line notice-bad">{error}</p>}
        <label className="fld">Name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required autoFocus placeholder="e.g. Network Plus" /></label>
        <div className="task-colours" role="radiogroup" aria-label="Colour">
          {COLOURS.map((c) => <button key={c} type="button" role="radio" aria-checked={colour === c} aria-label={c} className={`task-colour${colour === c ? ' on' : ''}`} style={{ background: c }} onClick={() => setColour(c)} />)}
        </div>
        {list && <p className="small muted">Deleting a list keeps its tasks; they move to Tasks.</p>}
      </form>
    </Modal>
  );
}

// The board: one column per stage, cards you drag between them.
function Board({ tasks, listOf, today, onOpen }) {
  const [over, setOver] = useState(null);
  const drop = (stage) => (e) => {
    e.preventDefault();
    setOver(null);
    const id = Number(e.dataTransfer.getData('text/plain'));
    const t = tasks.find((x) => x.id === id);
    if (t && t.stage !== stage) changeTask(id, { stage, done: stage === 'done' });
  };
  return (
    <div className="task-board">
      {STAGES.map((s) => {
        const cards = tasks.filter((t) => (t.stage || (t.done ? 'done' : 'todo')) === s.id);
        return (
          <section key={s.id} className={`task-col col-${s.id}${over === s.id ? ' over' : ''}`} aria-label={s.label}
            onDragOver={(e) => { e.preventDefault(); setOver(s.id); }} onDragLeave={() => setOver((o) => (o === s.id ? null : o))} onDrop={drop(s.id)}>
            <h3 className="task-col-head">{s.label} <span className="muted small">{cards.length}</span></h3>
            {!cards.length && <p className="small muted task-col-empty">Drag a card here</p>}
            {cards.map((t) => {
              const due = dueLabel(t, today);
              const list = listOf(t.listId);
              const stepsDone = t.steps.filter((x) => x.done).length;
              return (
                <article key={t.id} className={`task-card p-${t.priority}${t.done ? ' done' : ''}`} draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(t.id)); e.dataTransfer.effectAllowed = 'move'; }}>
                  <button type="button" className="task-card-open" onClick={() => onOpen(t)}>
                    {list && <span className="task-list-chip" style={{ '--list': list.colour }}>{list.name}</span>}
                    <span className="task-title">{t.title}</span>
                    <span className="task-meta">
                      {due && <span className={`task-due ${due.tone}`}>{due.text}</span>}
                      {t.priority !== 'none' && <span className={`task-flag p-${t.priority}`}><Flag size={12} aria-hidden="true" /> {PRIORITY_LABEL[t.priority]}</span>}
                      {t.steps.length > 0 && <span className="small muted">{stepsDone}/{t.steps.length} steps</span>}
                    </span>
                  </button>
                  {/* Keyboard and touch: move without dragging. */}
                  <select className="task-card-move" value={t.stage || 'todo'} aria-label={`Move "${t.title}"`} onChange={(e) => changeTask(t.id, { stage: e.target.value, done: e.target.value === 'done' })}>
                    {STAGES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                  </select>
                </article>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

export default function Tasks() {
  const { tasks, lists, status } = useTasks();
  const [view, setView] = useState('today'); // a view id, or 'list:<id>'
  const [q, setQ] = useState('');
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('today'); // quick add: today | tomorrow | week | none
  const [open, setOpen] = useState(null);
  const [editingList, setEditingList] = useState(null); // a list, or 'new'
  const [error, setError] = useState(null);
  const [layout, setLayoutState] = useState(() => { try { return localStorage.getItem(LAYOUT_KEY) === 'board' ? 'board' : 'list'; } catch { return 'list'; } });
  const setLayout = (v) => { setLayoutState(v); try { localStorage.setItem(LAYOUT_KEY, v); } catch { /* this visit only */ } };
  const input = useRef(null);
  const today = todayUk();

  const all = tasks || [];
  const listOf = (id) => lists.find((l) => l.id === id) || null;
  const currentList = view.startsWith('list:') ? listOf(Number(view.slice(5))) : null;
  const openTasks = all.filter((t) => !t.done);
  const counts = {
    today: openTasks.filter((t) => t.dueDate && t.dueDate <= today).length,
    upcoming: openTasks.filter((t) => t.dueDate && t.dueDate > today).length,
    anytime: openTasks.length,
    done: all.filter((t) => t.done).length,
  };

  // What's on screen, in groups.
  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const match = (t) => !term || t.title.toLowerCase().includes(term) || (t.notes || '').toLowerCase().includes(term);
    const open = openTasks.filter(match);
    if (term && view !== 'done') return [{ key: 'found', label: `Matching “${q.trim()}”`, tasks: open }];
    if (view === 'done') {
      return [{ key: 'done', label: null, tasks: all.filter((t) => t.done && match(t)).sort((a, b) => String(b.doneAt).localeCompare(String(a.doneAt))) }];
    }
    const byDate = (list) => {
      const late = list.filter((t) => t.dueDate && t.dueDate < today);
      const now = list.filter((t) => t.dueDate === today);
      const later = list.filter((t) => t.dueDate && t.dueDate > today);
      const none = list.filter((t) => !t.dueDate);
      return [
        { key: 'late', label: 'Overdue', tone: 'late', tasks: late },
        { key: 'now', label: 'Today', tasks: now },
        { key: 'later', label: 'Later', tasks: later },
        { key: 'none', label: 'No date', tasks: none },
      ];
    };
    if (view === 'today') return byDate(open.filter((t) => t.dueDate && t.dueDate <= today)).slice(0, 2);
    if (view === 'upcoming') {
      const days = [...new Set(open.filter((t) => t.dueDate > today).map((t) => t.dueDate))].sort();
      return days.map((d) => ({ key: d, label: d === addDays(today, 1) ? `Tomorrow · ${weekday(d)} ${formatDay(d)}` : `${weekday(d)} ${formatDay(d)}`, tasks: open.filter((t) => t.dueDate === d) }));
    }
    if (currentList) return byDate(open.filter((t) => t.listId === currentList.id));
    return byDate(open);
  }, [all, openTasks, view, q, today, currentList]);
  const shownCount = groups.reduce((n, g) => n + g.tasks.length, 0);
  // The board: what this view shows, plus what was finished in the last two
  // weeks (Completed shows every finished one).
  const boardTasks = useMemo(() => {
    const term = q.trim().toLowerCase();
    const match = (t) => !term || t.title.toLowerCase().includes(term) || (t.notes || '').toLowerCase().includes(term);
    const inView = (t) => {
      if (currentList) return t.listId === currentList.id;
      if (view === 'today') return t.dueDate && t.dueDate <= today;
      if (view === 'upcoming') return t.dueDate && t.dueDate > today;
      return true;
    };
    const since = addDays(today, -14);
    return all.filter((t) => match(t) && (t.done ? (view === 'done' || (inView(t) || view === 'today') && String(t.doneAt || '').slice(0, 10) >= since) : view !== 'done' && inView(t)));
  }, [all, q, view, currentList, today]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setError(null);
    const dueDate = { today, tomorrow: addDays(today, 1), week: addDays(today, 7), none: null }[due];
    try {
      await api('/tasks', { method: 'POST', body: { title: title.trim(), dueDate, listId: currentList?.id ?? null } });
      setTitle('');
      await reloadTasks();
      input.current?.focus();
    } catch (err) { setError(problem(err)); }
  };

  const heading = currentList ? currentList.name : VIEWS.find((v) => v.id === view)?.label || 'Tasks';
  const showAdd = view !== 'done';
  const pickView = (v) => {
    setView(v);
    setQ('');
    if (v === 'upcoming') setDue('tomorrow');
    else if (v === 'today') setDue('today');
    else setDue('none');
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Tasks</h1>
            <p className="muted">Your own to-do list. Only you see it. Add a task, give it a deadline, and tick it off.</p>
          </div>
        </div>
      </section>

      <div className="tasks-layout">
        <nav className="card tasks-side" aria-label="Task views">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" className={`tasks-view${view === v.id ? ' on' : ''}`} onClick={() => pickView(v.id)}>
              <v.icon size={18} aria-hidden="true" /> <span>{v.label}</span>
              <span className={`tasks-n${v.id === 'today' && counts.today ? ' due' : ''}`}>{counts[v.id] || ''}</span>
            </button>
          ))}
          <div className="tasks-side-head">
            <span>Lists</span>
            <button type="button" className="btn icon-only" aria-label="New list" onClick={() => setEditingList('new')}><Plus size={16} aria-hidden="true" /></button>
          </div>
          {!lists.length && <p className="small muted tasks-side-empty">Group tasks into lists, e.g. Network Plus or Fleet.</p>}
          {lists.map((l) => (
            <button key={l.id} type="button" className={`tasks-view${view === `list:${l.id}` ? ' on' : ''}`} onClick={() => pickView(`list:${l.id}`)} onDoubleClick={() => setEditingList(l)}>
              <span className="task-dot" style={{ background: l.colour }} aria-hidden="true" /> <span>{l.name}</span>
              <span className="tasks-n">{openTasks.filter((t) => t.listId === l.id).length || ''}</span>
            </button>
          ))}
        </nav>

        <section className="card tasks-main">
          <div className="tasks-main-head">
            <h2>{currentList && <span className="task-dot" style={{ background: currentList.colour }} aria-hidden="true" />}{heading}</h2>
            {currentList && <button type="button" className="link-btn" onClick={() => setEditingList(currentList)}>Edit list</button>}
            <div className="view-toggle tasks-layout-toggle" role="group" aria-label="Show as">
              <button type="button" className={`btn with-icon${layout === 'list' ? ' active' : ''}`} aria-pressed={layout === 'list'} onClick={() => setLayout('list')}><List size={16} aria-hidden="true" /> List</button>
              <button type="button" className={`btn with-icon${layout === 'board' ? ' active' : ''}`} aria-pressed={layout === 'board'} onClick={() => setLayout('board')}><Columns3 size={16} aria-hidden="true" /> Board</button>
            </div>
            <label className="defect-search tasks-search">
              <Search size={16} aria-hidden="true" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks" aria-label="Search tasks" />
            </label>
          </div>

          {showAdd && (
            <form className="task-add" onSubmit={add}>
              <Plus size={18} aria-hidden="true" />
              <input ref={input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={`Add a task${currentList ? ` to ${currentList.name}` : ''} and press Enter`} aria-label="New task" />
              <select value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due">
                <option value="today">Due today</option>
                <option value="tomorrow">Due tomorrow</option>
                <option value="week">Due next week</option>
                <option value="none">No date</option>
              </select>
              <button type="submit" className="btn primary" disabled={!title.trim()}>Add</button>
            </form>
          )}
          {error && <p className="notice-line notice-bad">{error}</p>}
          {status === 'loading' && !tasks && <p className="muted">Loading…</p>}
          {status === 'error' && !tasks && <p className="notice-line notice-bad">Your tasks couldn&apos;t be loaded. <button type="button" className="link-btn" onClick={reloadTasks}>Try again</button></p>}

          {tasks && layout === 'board' && <Board tasks={boardTasks} listOf={listOf} today={today} onOpen={setOpen} />}
          {tasks && layout === 'list' && !shownCount && (
            <div className="empty">
              {view === 'done' ? <Check size={32} aria-hidden="true" /> : <ListTodo size={32} aria-hidden="true" />}
              <p className="muted">{q.trim() ? 'No tasks match.' : view === 'today' ? 'Nothing due today.' : view === 'upcoming' ? 'Nothing coming up.' : view === 'done' ? 'Nothing completed yet.' : 'No tasks here yet.'}</p>
            </div>
          )}
          {layout === 'list' && groups.filter((g) => g.tasks.length).map((g) => (
            <div key={g.key} className="task-group">
              {g.label && <h3 className={`task-group-head${g.tone ? ` ${g.tone}` : ''}`}>{g.label} <span className="muted small">{g.tasks.length}</span></h3>}
              <ul className="task-list">
                {g.tasks.map((t) => <TaskRow key={t.id} t={t} list={currentList ? null : listOf(t.listId)} today={today} onOpen={setOpen} />)}
              </ul>
            </div>
          ))}
        </section>
      </div>

      {open && <TaskModal task={open} lists={lists} onClose={() => setOpen(null)} />}
      {editingList && <ListModal list={editingList === 'new' ? null : editingList} onClose={(r) => { if (r === 'deleted') setView('anytime'); setEditingList(null); }} />}
    </div>
  );
}
